// File: frontend/shared/theme.js
import { createTheme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';

// Core constants for easier theming
const core = {
  borderRadius: 12,
  cardPadding: 4,
  fontWeightBold: 700,
  fontWeightSemi: 600,
  transition: 'all 0.3s ease'
};

// Professional theme with proper dark mode support - Enhanced version
const createProfessionalTheme = (mode = 'light') => ({
  mode,
  primary: {
    main: mode === 'dark' ? '#60A5FA' : '#2563EB',
    contrastText: '#FFFFFF',
    dark: mode === 'dark' ? '#3B82F6' : '#1D4ED8',
    light: mode === 'dark' ? '#93C5FD' : '#60A5FA'
  },
  secondary: {
    main: mode === 'dark' ? '#00CC88' : '#FFBF00',
    contrastText: '#FFFFFF',
    dark: mode === 'dark' ? '#00B377' : '#E6AC00',
    light: mode === 'dark' ? '#33DDA0' : '#FFD54A'
  },
  background: {
    default: mode === 'dark' ? '#0A0A0A' : '#F9FAFB',
    paper: mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
    elevated: mode === 'dark' ? '#262626' : '#FFFFFF'
  },
  text: {
    primary: mode === 'dark' ? '#EDEDED' : '#171717',
    secondary: mode === 'dark' ? '#D4D4D8' : '#4B5563',
    disabled: mode === 'dark' ? '#999999' : '#9CA3AF'
  },
  accent: {
    main: mode === 'dark' ? '#4ADE80' : '#22C55E',
    light: mode === 'dark' ? '#86EFAC' : '#86EFAC'
  },
  info: {
    main: mode === 'dark' ? '#60A5FA' : '#3B82F6',
    light: mode === 'dark' ? 'rgba(96, 165, 250, 0.1)' : 'rgba(59, 130, 246, 0.1)',
    dark: mode === 'dark' ? '#3B82F6' : '#2563EB'
  },
  success: {
    main: mode === 'dark' ? '#4ADE80' : '#22C55E',
    light: mode === 'dark' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(34, 197, 94, 0.1)',
    dark: mode === 'dark' ? '#22C55E' : '#16A34A',
    contrastText: '#FFFFFF'
  },
  error: {
    main: mode === 'dark' ? '#F97373' : '#EF4444',
    light: mode === 'dark' ? 'rgba(249, 115, 115, 0.1)' : 'rgba(239, 68, 68, 0.1)',
    dark: mode === 'dark' ? '#EF4444' : '#DC2626'
  },
  warning: {
    main: mode === 'dark' ? '#FBBF24' : '#F59E0B',
    light: mode === 'dark' ? 'rgba(251, 191, 36, 0.1)' : 'rgba(245, 158, 11, 0.1)',
    dark: mode === 'dark' ? '#F59E0B' : '#D97706'
  },
  divider: mode === 'dark' ? '#333333' : '#E5E7EB',
  action: {
    hover: mode === 'dark' ? 'rgba(96, 165, 250, 0.1)' : '#F3F4F6',
    selected: mode === 'dark' ? 'rgba(96, 165, 250, 0.2)' : '#EFF6FF',
    disabled: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
    disabledBackground: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
  }
});

const palette = createProfessionalTheme('light'); // Default to light mode

const typography = {
  fontFamily: `'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', sans-serif`,
  // Professional heading styles - Colors will be overridden by theme components for dark mode
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.025em'
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.025em'
  },
  h3: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4
  },
  h4: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4
  },
  h5: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.4
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.4
  },
  // Professional body text
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
    color: palette.text.primary,
    fontWeight: 400
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: palette.text.secondary,
    fontWeight: 400
  },
  // Professional button text
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.025em'
  },
  // Professional caption
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.4,
    color: palette.text.secondary,
    fontWeight: 400
  },
  // Professional overline
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: palette.text.secondary
  },
  // Custom professional styles
  heroHeading: {
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 700,
    lineHeight: 1.2,
    color: palette.text.primary,
    letterSpacing: '-0.025em',
    maxWidth: '800px',
    marginBottom: '1.5rem'
  },
  heroSubtitle: {
    fontSize: 'clamp(1rem, 2vw, 1.25rem)',
    color: palette.text.secondary,
    lineHeight: 1.6,
    maxWidth: '600px',
    marginBottom: '2rem',
    fontWeight: 400
  },
  sectionTitle: {
    fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: '3rem',
    color: palette.text.primary,
    letterSpacing: '-0.025em'
  },
  ctaHeading: {
    fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
    fontWeight: 600,
    marginBottom: '1.5rem',
    color: palette.text.primary
  },
  ctaText: {
    fontSize: '1.125rem',
    lineHeight: 1.6,
    color: palette.text.secondary,
    fontWeight: 400
  }
};

const componentStyles = {
  MuiButton: {
    variants: [
      {
        props: { variant: 'gradient' },
        style: {
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          color: palette.primary.contrastText,
          borderRadius: '8px',
          padding: '12px 24px',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
          '&:hover': {
            background: `linear-gradient(135deg, ${palette.primary.dark} 0%, ${palette.primary.main} 100%)`,
            boxShadow: '0 4px 8px rgba(37, 99, 235, 0.3)'
          }
        }
      }
    ]
  },
  ctaCard: {
    root: {
      padding: '24px',
      backgroundColor: palette.background.paper,
      borderRadius: '12px',
      border: `1px solid ${palette.divider}`,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
      textAlign: 'center',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
        transform: 'translateY(-1px)'
      }
    },
    title: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: palette.text.primary,
      marginBottom: '1rem'
    },
    description: {
      color: palette.text.secondary,
      fontSize: '1rem',
      lineHeight: 1.6,
      marginBottom: '1.5rem'
    },
    button: {
      backgroundColor: palette.primary.main,
      color: palette.primary.contrastText,
      textTransform: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: palette.primary.dark
      }
    }
  },
  stepCard: {
    root: {
      padding: core.cardPadding,
      backgroundColor: palette.background.paper,
      borderRadius: core.borderRadius,
      boxShadow: `0 4px 14px rgba(37, 99, 235, 0.22)`,
      textAlign: 'center',
      transition: core.transition,
      '&:hover': {
        boxShadow: `0 8px 24px rgba(37, 99, 235, 0.3)`
      }
    },
    title: {
      fontWeight: core.fontWeightBold,
      marginTop: '1rem'
    },
    description: {
      color: palette.text.secondary,
      marginTop: '0.5rem'
    }
  },
  metricCard: {
    root: {
      textAlign: 'center',
      padding: core.cardPadding,
      backgroundColor: palette.background.paper,
      borderRadius: core.borderRadius
    },
    value: {
      fontWeight: '700',
      color: palette.secondary.main,
      mb: '8px',
    },
    label: {
      fontWeight: '600',
      color: palette.text.primary,
      mb: '4px',
    },
    subLabel: {
      color: palette.text.secondary,
      fontSize: '0.9rem',
    },
  },
  caseStudyCard: {
    root: {
      padding: core.cardPadding,
      border: `1px solid ${palette.divider}`,
      borderRadius: 8
    },
    title: {
      fontWeight: 'bold',
      marginBottom: '8px'
    },
    excerpt: {
      color: palette.text.secondary,
      marginBottom: '16px'
    },
    button: {
      textTransform: 'none',
      color: palette.primary.main
    }
  },
  testimonialCard: {
    root: {
      padding: core.cardPadding,
      border: `1px solid ${palette.divider}`,
      borderRadius: 8
    },
    text: {
      fontStyle: 'italic',
      marginBottom: '16px'
    },
    author: {
      display: 'flex',
      alignItems: 'center'
    },
    avatar: {
      borderRadius: '50%',
      marginRight: '8px'
    }
  },
  blogCard: {
    root: {
      padding: core.cardPadding,
      backgroundColor: palette.background.paper,
      borderRadius: 8,
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    media: {
      position: 'relative',
      height: '200px',
      marginBottom: '16px'
    },
    image: {
      borderRadius: 8,
      objectFit: 'cover'
    },
    content: {
      textAlign: 'left'
    },
    title: {
      fontWeight: 'bold',
      marginBottom: '8px'
    },
    excerpt: {
      color: palette.text.secondary,
      marginBottom: '16px'
    },
    button: {
      color: palette.primary.main,
      textTransform: 'none'
    }
  },
  heroSlider: {
    root: {
      position: 'relative',
      overflow: 'hidden'
    },
    image: {
      objectFit: 'cover'
    },
    content: {
      position: 'absolute',
      bottom: '20%',
      left: '10%',
      color: palette.primary.contrastText
    },
    title: {
      fontSize: '2rem',
      fontWeight: 'bold'
    },
    subtitle: {
      fontSize: '1.2rem',
      marginBottom: '16px'
    },
    button: {
      background: `linear-gradient(135deg, ${palette.primary.main}, ${palette.secondary.main})`,
      color: palette.primary.contrastText,
      borderRadius: '30px',
      px: 4,
      py: 1.5,
      fontWeight: 600,
      '&:hover': {
        background: `linear-gradient(135deg, ${palette.primary.dark}, ${palette.secondary.dark})`
      }
    },
    dots: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '16px'
    },
    dot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: palette.divider,
      margin: '0 4px',
      cursor: 'pointer'
    },
    activeDot: {
      background: palette.primary.main
    }
  }
};

// Create theme function that accepts mode
const createThemeWithMode = (mode = 'light') => {
  const dynamicPalette = createProfessionalTheme(mode);

  return createTheme({
    palette: dynamicPalette,
    breakpoints: {
      values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 }
    },
    typography,
    shape: {
      borderRadius: 8 // Professional rounded corners
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            color: mode === 'dark' ? dynamicPalette.text.primary : undefined
          },
          'h1, h2, h3, h4, h5, h6': {
            color: mode === 'dark' ? dynamicPalette.text.primary : undefined
          },
          'p, span, label, small': {
            color: mode === 'dark' ? dynamicPalette.text.secondary : undefined
          }
        }
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? dynamicPalette.text.primary : undefined
          },
          h1: {
            color: dynamicPalette.text.primary,
            fontWeight: 700
          },
          h2: {
            color: dynamicPalette.text.primary,
            fontWeight: 700
          },
          h3: {
            color: dynamicPalette.text.primary,
            fontWeight: 600
          },
          h4: {
            color: dynamicPalette.text.primary,
            fontWeight: 600
          },
          h5: {
            color: dynamicPalette.text.primary,
            fontWeight: 600
          },
          h6: {
            color: dynamicPalette.text.primary,
            fontWeight: 600
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            padding: '12px 24px',
            fontSize: '0.875rem',
            boxShadow: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : undefined,
              boxShadow: mode === 'dark'
                ? '0 4px 12px rgba(96, 165, 250, 0.28)'
                : '0 2px 8px rgba(0, 0, 0, 0.15)'
            }
          },
          contained: {
            background: mode === 'dark'
              ? `linear-gradient(135deg, ${dynamicPalette.primary.main} 0%, ${dynamicPalette.primary.dark} 100%)`
              : dynamicPalette.primary.main,
            color: dynamicPalette.primary.contrastText,
            boxShadow: mode === 'dark'
              ? '0 4px 12px rgba(96, 165, 250, 0.25), 0 0 0 1px rgba(96, 165, 250, 0.28)'
              : 'none',
            '&:hover': {
              background: mode === 'dark'
                ? `linear-gradient(135deg, ${dynamicPalette.primary.dark} 0%, #1D4ED8 100%)`
                : dynamicPalette.primary.dark,
              boxShadow: mode === 'dark'
                ? '0 6px 20px rgba(96, 165, 250, 0.32), 0 0 0 1px rgba(96, 165, 250, 0.35)'
                : '0 4px 12px rgba(0, 0, 0, 0.2)'
            },
            '&:active': {
              transform: 'translateY(0px)'
            }
          },
          outlined: {
            borderColor: mode === 'dark'
              ? 'rgba(96, 165, 250, 0.45)'
              : dynamicPalette.primary.main,
            borderWidth: '1.5px',
            color: dynamicPalette.primary.main,
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? dynamicPalette.info.light
                : dynamicPalette.action.hover,
              borderColor: dynamicPalette.primary.main,
              borderWidth: '1.5px'
            }
          },
          outlinedError: {
            color: dynamicPalette.error.main,
            borderColor: mode === 'dark' ? 'rgba(249, 115, 115, 0.55)' : dynamicPalette.error.main,
            '&:hover': {
              backgroundColor: mode === 'dark' ? dynamicPalette.error.light : 'rgba(239, 68, 68, 0.08)',
              borderColor: mode === 'dark' ? 'rgba(249, 115, 115, 0.75)' : dynamicPalette.error.dark
            }
          },
          text: {
            color: dynamicPalette.primary.main,
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? dynamicPalette.info.light
                : dynamicPalette.action.hover
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: dynamicPalette.text.primary
          },
          input: {
            color: dynamicPalette.text.primary,
            '::placeholder': {
              color: mode === 'dark' ? dynamicPalette.text.disabled : undefined,
              opacity: 1
            }
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: mode === 'dark'
              ? 'rgba(255,255,255,0.76)'
              : dynamicPalette.text.secondary,
            fontWeight: 500,
            '&.Mui-focused': {
              color: mode === 'dark'
                ? dynamicPalette.primary.light
                : dynamicPalette.primary.main
            },
            '&.Mui-error': {
              color: dynamicPalette.error.main
            },
            '&.Mui-disabled': {
              color: dynamicPalette.text.disabled
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: mode === 'dark'
              ? 'rgba(255,255,255,0.76)'
              : dynamicPalette.text.secondary,
            '&.Mui-focused': {
              color: mode === 'dark'
                ? dynamicPalette.primary.light
                : dynamicPalette.primary.main
            },
            '&.Mui-error': {
              color: dynamicPalette.error.main
            },
            '&.Mui-disabled': {
              color: dynamicPalette.text.disabled
            }
          },
          shrink: {
            backgroundColor: dynamicPalette.background.paper,
            paddingLeft: 4,
            paddingRight: 4
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: mode === 'dark'
              ? `1px solid rgba(96, 165, 250, 0.16)`
              : `1px solid ${dynamicPalette.divider}`,
            boxShadow: mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(96, 165, 250, 0.1)'
              : '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
            backgroundColor: dynamicPalette.background.paper,
            backgroundImage: mode === 'dark'
              ? 'linear-gradient(145deg, rgba(96, 165, 250, 0.04) 0%, rgba(26, 26, 26, 1) 100%)'
              : 'none'
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: mode === 'dark'
              ? `1px solid rgba(96, 165, 250, 0.16)`
              : `1px solid ${dynamicPalette.divider}`,
            boxShadow: mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(96, 165, 250, 0.1)'
              : '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
            backgroundColor: dynamicPalette.background.paper,
            backgroundImage: mode === 'dark'
              ? 'linear-gradient(145deg, rgba(96, 165, 250, 0.04) 0%, rgba(26, 26, 26, 1) 100%)'
              : 'none',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(96, 165, 250, 0.18)'
                : '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
              transform: 'translateY(-2px)'
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 20, // More rounded for modern look
            fontWeight: 600,
            fontSize: '0.8125rem', // Slightly larger for better readability
            padding: '6px 12px',
            height: 'auto',
            minHeight: 28,
            transition: 'all 0.2s ease',
            // Default styling for all chips
            backgroundColor: mode === 'dark'
              ? 'rgba(96, 165, 250, 0.14)'
              : 'rgba(59, 130, 246, 0.08)',
            color: mode === 'dark'
              ? dynamicPalette.primary.light
              : dynamicPalette.primary.dark,
            border: mode === 'dark'
              ? '1px solid rgba(96, 165, 250, 0.24)'
              : '1px solid rgba(59, 130, 246, 0.2)',
            boxShadow: mode === 'dark'
              ? '0 2px 4px rgba(0, 0, 0, 0.2)'
              : '0 1px 3px rgba(0, 0, 0, 0.08)',
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? 'rgba(96, 165, 250, 0.2)'
                : 'rgba(59, 130, 246, 0.12)',
              boxShadow: mode === 'dark'
                ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                : '0 2px 6px rgba(0, 0, 0, 0.12)',
              transform: 'translateY(-1px)'
            }
          },
          label: {
            padding: '0 8px',
            fontWeight: 600,
            letterSpacing: '0.02em'
          },
          icon: {
            color: mode === 'dark' ? dynamicPalette.primary.main : dynamicPalette.info.main,
            marginLeft: '6px',
            marginRight: '-4px',
            fontSize: '1rem'
          },
          filled: {
            backgroundColor: mode === 'dark'
              ? 'rgba(96, 165, 250, 0.2)'
              : 'rgba(59, 130, 246, 0.15)',
            color: mode === 'dark'
              ? dynamicPalette.primary.light
              : dynamicPalette.primary.dark,
            border: mode === 'dark'
              ? '1px solid rgba(96, 165, 250, 0.32)'
              : '1px solid rgba(59, 130, 246, 0.25)',
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? 'rgba(96, 165, 250, 0.28)'
                : 'rgba(59, 130, 246, 0.2)'
            }
          },
          outlined: {
            borderColor: mode === 'dark'
              ? 'rgba(96, 165, 250, 0.45)'
              : 'rgba(59, 130, 246, 0.4)',
            borderWidth: '1.5px',
            color: mode === 'dark'
              ? dynamicPalette.primary.light
              : dynamicPalette.primary.dark,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? 'rgba(96, 165, 250, 0.08)'
                : 'rgba(59, 130, 246, 0.05)',
              borderColor: mode === 'dark'
                ? 'rgba(96, 165, 250, 0.55)'
                : 'rgba(59, 130, 246, 0.5)'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              backgroundColor: mode === 'dark'
                ? 'rgba(255, 255, 255, 0.06)'
                : dynamicPalette.background.paper,
              backdropFilter: mode === 'dark' ? 'blur(8px)' : 'none',
              transition: 'all 0.3s ease',
              '& fieldset': {
                borderColor: mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.22)'
                  : dynamicPalette.divider,
                borderWidth: '1.5px'
              },
              '&:hover': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : dynamicPalette.background.paper,
                '& fieldset': {
                  borderColor: mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.34)'
                    : dynamicPalette.primary.main
                }
              },
              '&.Mui-focused': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : dynamicPalette.background.paper,
                boxShadow: mode === 'dark'
                  ? '0 0 0 3px rgba(96, 165, 250, 0.12)'
                  : 'none',
                '& fieldset': {
                  borderColor: dynamicPalette.primary.main,
                  borderWidth: '2px'
                }
              }
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark'
                ? 'rgba(255,255,255,0.75)'
                : dynamicPalette.text.primary,
              '&.Mui-focused': {
                color: mode === 'dark' ? dynamicPalette.primary.light : dynamicPalette.primary.main
              }
            },
            '& .MuiInputBase-input': {
              color: mode === 'dark' ? dynamicPalette.text.primary : dynamicPalette.text.primary,
              '&::placeholder': {
                color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : dynamicPalette.text.disabled,
                opacity: 1
              }
            }
          }
        }
      },
      // Ensure all outlined inputs (not only TextField) get the clearer default contrast
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? dynamicPalette.text.primary : undefined,
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : undefined,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.22)' : undefined
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.34)' : undefined
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? dynamicPalette.primary.main : undefined
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            color: dynamicPalette.text.primary,
            backgroundColor: mode === 'dark'
              ? 'rgba(255,255,255,0.06)'
              : dynamicPalette.background.paper,
            '&:focus': {
              backgroundColor: mode === 'dark'
                ? 'rgba(255,255,255,0.1)'
                : dynamicPalette.background.paper
            }
          },
          icon: {
            color: mode === 'dark'
              ? dynamicPalette.text.secondary
              : dynamicPalette.text.secondary
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark'
              ? dynamicPalette.background.elevated
              : dynamicPalette.background.paper,
            backgroundImage: mode === 'dark'
              ? 'linear-gradient(145deg, rgba(96, 165, 250, 0.06) 0%, rgba(26, 26, 26, 1) 100%)'
              : 'none',
            color: dynamicPalette.text.primary,
            border: mode === 'dark'
              ? '1px solid rgba(96, 165, 250, 0.22)'
              : `1px solid ${dynamicPalette.divider}`,
            boxShadow: mode === 'dark'
              ? '0 16px 44px rgba(0, 0, 0, 0.72), 0 0 0 1px rgba(96, 165, 250, 0.12)'
              : '0 12px 28px rgba(15, 23, 42, 0.12)'
          },
          list: {
            padding: '6px'
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            color: dynamicPalette.text.primary,
            minHeight: 38,
            '& .MuiTypography-root, & .MuiListItemText-primary': {
              color: dynamicPalette.text.primary
            },
            '& .MuiListItemText-secondary': {
              color: dynamicPalette.text.secondary
            },
            '&:hover, &.Mui-focusVisible': {
              backgroundColor: mode === 'dark'
                ? 'rgba(96, 165, 250, 0.16)'
                : 'rgba(59, 130, 246, 0.08)',
              color: mode === 'dark'
                ? dynamicPalette.primary.light
                : dynamicPalette.primary.dark
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'dark'
                ? 'rgba(96, 165, 250, 0.24)'
                : 'rgba(59, 130, 246, 0.14)',
              color: mode === 'dark'
                ? dynamicPalette.primary.light
                : dynamicPalette.primary.dark,
              '&:hover': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(96, 165, 250, 0.3)'
                  : 'rgba(59, 130, 246, 0.18)'
              }
            }
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark'
              ? dynamicPalette.background.paper
              : dynamicPalette.background.paper,
            backgroundImage: mode === 'dark'
              ? 'linear-gradient(145deg, rgba(96, 165, 250, 0.08) 0%, rgba(26, 26, 26, 0.98) 100%)'
              : 'none',
            border: mode === 'dark'
              ? '2px solid rgba(96, 165, 250, 0.28)'
              : `1px solid ${dynamicPalette.divider}`,
            boxShadow: mode === 'dark'
              ? '0 12px 40px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(96, 165, 250, 0.25)'
              : '0 4px 16px rgba(0, 0, 0, 0.1)',
            backdropFilter: mode === 'dark' ? 'blur(16px)' : 'none',
            borderRadius: 12,
            marginTop: 8,
            maxHeight: '400px'
          },
          listbox: {
            padding: '12px',
            backgroundColor: 'transparent',
            maxHeight: '320px',
            '& .MuiAutocomplete-option': {
              borderRadius: 8,
              margin: '4px 0',
              padding: '12px 16px',
              fontSize: '0.95rem',
              color: mode === 'dark'
                ? dynamicPalette.text.primary
                : dynamicPalette.text.primary,
              fontWeight: 500,
              transition: 'all 0.2s ease',
              minHeight: '44px',
              '&:hover': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(96, 165, 250, 0.2)'
                  : 'rgba(59, 130, 246, 0.08)',
                transform: 'translateX(6px)',
                color: mode === 'dark' ? dynamicPalette.primary.light : dynamicPalette.primary.main
              },
              '&[aria-selected="true"]': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(96, 165, 250, 0.28)'
                  : 'rgba(59, 130, 246, 0.15)',
                color: mode === 'dark' ? dynamicPalette.primary.main : dynamicPalette.primary.main,
                fontWeight: 700,
                boxShadow: mode === 'dark'
                  ? '0 2px 8px rgba(96, 165, 250, 0.2)'
                  : 'none',
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(96, 165, 250, 0.35)'
                    : 'rgba(59, 130, 246, 0.2)'
                }
              },
              '&.Mui-focused': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(96, 165, 250, 0.14)'
                  : 'rgba(59, 130, 246, 0.08)'
              }
            }
          },
          noOptions: {
            color: mode === 'dark'
              ? dynamicPalette.text.secondary
              : dynamicPalette.text.secondary,
            padding: '20px',
            fontSize: '0.9rem'
          },
          loading: {
            color: mode === 'dark'
              ? dynamicPalette.text.secondary
              : dynamicPalette.text.secondary,
            padding: '20px',
            fontSize: '0.9rem'
          },
          popupIndicator: {
            color: mode === 'dark'
              ? dynamicPalette.primary.main
              : dynamicPalette.primary.main,
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? 'rgba(96, 165, 250, 0.1)'
                : 'rgba(59, 130, 246, 0.05)'
            }
          },
          clearIndicator: {
            color: mode === 'dark'
              ? dynamicPalette.text.secondary
              : dynamicPalette.text.secondary,
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? 'rgba(148, 163, 184, 0.1)'
                : 'rgba(0, 0, 0, 0.05)',
              color: mode === 'dark' ? dynamicPalette.text.primary : dynamicPalette.text.primary
            }
          }
        }
      }
    },
    customComponents: componentStyles,
    // Profile Form Specific Styles - Reusable across all profile/form components
    profileForm: {
      // Container for editing forms (Education, Experience, etc.)
      editingContainer: (mode) => ({
        backgroundColor: mode === 'dark' ? dynamicPalette.background.elevated : '#f8fafc',
        border: mode === 'dark' ? `2px solid ${dynamicPalette.divider}` : '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        padding: '1rem'
      }),
      // Floating label styles (for field labels like "DEGREE", "INSTITUTION", etc.)
      floatingLabel: (mode, hasValue) => ({
        position: 'absolute',
        left: '0.875rem',
        top: hasValue ? '-0.5rem' : '50%',
        transform: hasValue ? 'translateY(0)' : 'translateY(-50%)',
        fontSize: '0.65rem',
        fontWeight: 600,
        color: mode === 'dark' ? dynamicPalette.text.secondary : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        transition: 'all 0.2s ease-in-out',
        pointerEvents: 'none',
        backgroundColor: mode === 'dark' ? dynamicPalette.background.elevated : '#f8fafc',
        padding: '0 0.25rem',
        zIndex: 2
      }),
      // Description field floating label (slightly different positioning)
      descriptionLabel: (mode, hasValue) => ({
        position: 'absolute',
        left: '0.875rem',
        top: hasValue ? '0.5rem' : '1rem',
        fontSize: hasValue ? '0.65rem' : '0.875rem',
        fontWeight: 600,
        color: mode === 'dark' ? dynamicPalette.text.secondary : '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        transition: 'all 0.2s ease-in-out',
        pointerEvents: 'none',
        backgroundColor: hasValue ? (mode === 'dark' ? dynamicPalette.background.elevated : '#f8fafc') : 'transparent',
        padding: hasValue ? '0 0.25rem' : '0',
        zIndex: 1
      }),
      // TextField styles for form fields
      textField: (mode, borderColor = null) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          '& input': {
            padding: '0.875rem'
          }
        },
        '& .MuiInputLabel-root': {
          display: 'none'
        },
        '& fieldset': {
          borderWidth: '2px',
          borderColor: mode === 'dark' ? dynamicPalette.divider : '#e2e8f0',
          transition: 'all 0.2s'
        },
        '& .MuiOutlinedInput-root:hover fieldset': {
          borderColor: borderColor || (mode === 'dark' ? dynamicPalette.success.main : '#22C55E')
        },
        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
          borderColor: borderColor || '#22C55E',
          boxShadow: borderColor
            ? `0 0 0 3px ${borderColor}10`
            : '0 0 0 3px rgba(34, 197, 94, 0.1)'
        }
      }),
      // TextField styles for experience fields (blue theme)
      textFieldBlue: (mode) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          '& input': {
            padding: '0.875rem'
          }
        },
        '& .MuiInputLabel-root': {
          display: 'none'
        },
        '& fieldset': {
          borderWidth: '2px',
          borderColor: mode === 'dark' ? dynamicPalette.divider : '#e2e8f0',
          transition: 'all 0.2s'
        },
        '& .MuiOutlinedInput-root:hover fieldset': {
          borderColor: mode === 'dark' ? dynamicPalette.primary.main : '#3B82F6'
        },
        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
          borderColor: '#3B82F6',
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
        }
      }),
      // Present Button for Education (Green theme)
      presentButtonEducation: (mode, isActive) => ({
        minWidth: '100px',
        height: '56px',
        borderRadius: '0.5rem',
        textTransform: 'none',
        fontWeight: 500,
        border: isActive
          ? '2px solid #22C55E'
          : mode === 'dark' ? `2px solid ${dynamicPalette.divider}` : '2px solid #e2e8f0',
        backgroundColor: isActive
          ? (mode === 'dark' ? 'rgba(74, 222, 128, 0.18)' : '#ecfdf5')
          : mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white',
        color: isActive
          ? (mode === 'dark' ? dynamicPalette.success.main : '#22C55E')
          : mode === 'dark' ? dynamicPalette.text.secondary : '#64748b',
        '&:hover': {
          backgroundColor: isActive
            ? (mode === 'dark' ? 'rgba(74, 222, 128, 0.26)' : '#d1fae5')
            : (mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f0fdf4'),
          borderColor: '#22C55E'
        }
      }),
      // Present Button for Experience (Blue theme)
      presentButtonExperience: (mode, isActive) => ({
        minWidth: '100px',
        height: '56px',
        borderRadius: '0.5rem',
        textTransform: 'none',
        fontWeight: 500,
        border: isActive
          ? '2px solid #3B82F6'
          : mode === 'dark' ? `2px solid ${dynamicPalette.divider}` : '2px solid #e2e8f0',
        backgroundColor: isActive
          ? (mode === 'dark' ? 'rgba(96, 165, 250, 0.18)' : '#eff6ff')
          : mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white',
        color: isActive
          ? (mode === 'dark' ? dynamicPalette.primary.light : '#3B82F6')
          : mode === 'dark' ? dynamicPalette.text.secondary : '#64748b',
        '&:hover': {
          backgroundColor: isActive
            ? (mode === 'dark' ? 'rgba(96, 165, 250, 0.26)' : '#dbeafe')
            : (mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f8fafc'),
          borderColor: '#3B82F6'
        }
      }),
      // Action buttons (Save Changes, Cancel)
      saveButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.5rem 1rem',
        backgroundColor: '#22C55E',
        color: 'white',
        border: 'none',
        borderRadius: '0.375rem',
        textTransform: 'none',
        fontSize: '0.875rem',
        '&:hover': {
          backgroundColor: '#16A34A'
        }
      },
      cancelButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.5rem 1rem',
        backgroundColor: '#6b7280',
        color: 'white',
        border: 'none',
        borderRadius: '0.375rem',
        textTransform: 'none',
        fontSize: '0.875rem',
        '&:hover': {
          backgroundColor: '#4b5563'
        }
      },
      // Add Education/Experience buttons
      addButton: (mode) => ({
        borderRadius: '0.75rem',
        px: 4,
        py: 2,
        borderWidth: '2px',
        borderStyle: 'dashed',
        borderColor: '#9CA3AF',
        color: '#6B7280',
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.95rem',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: '#2563EB',
          color: '#2563EB',
          backgroundColor: mode === 'dark' ? 'rgba(96, 165, 250, 0.1)' : '#EFF6FF',
          borderWidth: '2px',
          borderStyle: 'dashed',
        }
      }),
      // Card for displaying education/experience items (read-only view)
      displayCard: (mode) => ({
        mb: 3,
        borderRadius: '0.5rem',
        boxShadow: 'none',
        backgroundColor: mode === 'dark' ? dynamicPalette.background.elevated : '#ffffff',
        border: mode === 'dark' ? `1px solid ${dynamicPalette.divider}` : '1px solid #e2e8f0',
        transition: 'box-shadow 0.2s',
        position: 'relative',
        '&:hover': {
          boxShadow: mode === 'dark'
            ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
            : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          '& .education-actions, & .experience-actions': {
            opacity: 1
          }
        }
      }),
      // Edit/Delete action buttons (hover to show)
      editActionButton: (mode) => ({
        p: '0.375rem',
        color: mode === 'dark' ? dynamicPalette.primary.main : '#3B82F6',
        backgroundColor: mode === 'dark' ? 'rgba(96, 165, 250, 0.15)' : '#eff6ff',
        borderRadius: '0.25rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: mode === 'dark' ? 'rgba(96, 165, 250, 0.25)' : '#dbeafe'
        }
      }),
      deleteActionButton: (mode) => ({
        p: '0.375rem',
        color: mode === 'dark' ? '#f87171' : '#dc2626',
        backgroundColor: mode === 'dark' ? 'rgba(220, 38, 38, 0.15)' : '#fef2f2',
        borderRadius: '0.25rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: mode === 'dark' ? 'rgba(220, 38, 38, 0.25)' : '#fee2e2'
        }
      }),
      // Section title (Education, Experience headings)
      sectionTitle: (mode) => ({
        fontSize: '1.5rem',
        fontWeight: 600,
        color: mode === 'dark' ? dynamicPalette.text.primary : '#1F2937',
        mb: 3
      }),
      // Personal information field styles
      personalInfoField: (mode) => ({
        fontSize: '0.875rem',
        color: mode === 'dark' ? dynamicPalette.text.primary : '#111827',
        fontWeight: 500,
        wordBreak: 'break-words',
        cursor: 'pointer',
        padding: '0.375rem',
        borderRadius: 1,
        transition: 'background-color 0.2s',
        '&:hover': {
          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'
        }
      }),
      // "Not provided" placeholder text
      notProvidedText: (mode) => ({
        color: mode === 'dark' ? '#64748b' : '#9ca3af',
        fontStyle: 'italic'
      }),
      // Gmail/Settings Card (Preferences step)
      settingsCard: (mode) => ({
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        backgroundColor: mode === 'dark' ? dynamicPalette.background.elevated : '#ffffff',
        border: mode === 'dark' ? `1px solid ${dynamicPalette.divider}` : '1px solid #E5E7EB',
        p: 4
      })
    },
    layout: {
      root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: dynamicPalette.background.default,
        color: dynamicPalette.text.primary
      },
      hero: {
        paddingTop: '80px',
        background: `linear-gradient(135deg, ${dynamicPalette.background.default} 0%, ${dynamicPalette.background.paper} 100%)`
      },
      clientSection: {
        py: 8,
        background: dynamicPalette.background.paper,
        borderTop: `1px solid ${dynamicPalette.divider}`,
        borderBottom: `1px solid ${dynamicPalette.divider}`
      },
      ctaSection: {
        py: 8,
        background: `linear-gradient(135deg, ${dynamicPalette.primary.main} 0%, ${dynamicPalette.primary.dark} 100%)`,
        color: dynamicPalette.primary.contrastText
      },
      sectionDark: {
        py: 8,
        background: dynamicPalette.background.default
      },
      sectionLight: {
        py: 8,
        background: dynamicPalette.background.paper
      }
    }
  });
};

// Create default theme (light mode)
const theme = createThemeWithMode('light');

// Export a getAppTheme function for compatibility with ThemeProvider
export function getAppTheme(mode = 'light') {
  // Return dynamic theme based on mode
  return createThemeWithMode(mode);
}

// Export the theme creation function for dynamic switching
export { createThemeWithMode };

// Helper function to get profile form styles
export function getProfileFormStyles(theme) {
  const mode = theme.palette.mode;
  return theme.profileForm || {
    editingContainer: (m = mode) => ({
      backgroundColor: m === 'dark' ? '#262626' : '#f8fafc',
      border: m === 'dark' ? '2px solid #333333' : '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '1rem'
    }),
    floatingLabel: (m = mode, hasValue) => ({
      position: 'absolute',
      left: '0.875rem',
      top: hasValue ? '-0.5rem' : '50%',
      transform: hasValue ? 'translateY(0)' : 'translateY(-50%)',
      fontSize: '0.65rem',
      fontWeight: 600,
      color: m === 'dark' ? '#D4D4D8' : '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      transition: 'all 0.2s ease-in-out',
      pointerEvents: 'none',
      backgroundColor: m === 'dark' ? '#262626' : '#f8fafc',
      padding: '0 0.25rem',
      zIndex: 2
    }),
    descriptionLabel: (m = mode, hasValue) => ({
      position: 'absolute',
      left: '0.875rem',
      top: hasValue ? '0.5rem' : '1rem',
      fontSize: hasValue ? '0.65rem' : '0.875rem',
      fontWeight: 600,
      color: m === 'dark' ? '#D4D4D8' : '#94a3b8',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      transition: 'all 0.2s ease-in-out',
      pointerEvents: 'none',
      backgroundColor: hasValue ? (m === 'dark' ? '#262626' : '#f8fafc') : 'transparent',
      padding: hasValue ? '0 0.25rem' : '0',
      zIndex: 1
    }),
    textField: (m = mode, borderColor = null) => ({
      '& .MuiOutlinedInput-root': {
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        '& input': {
          padding: '0.875rem'
        }
      },
      '& .MuiInputLabel-root': {
        display: 'none'
      },
      '& fieldset': {
        borderWidth: '2px',
        borderColor: m === 'dark' ? '#333333' : '#e2e8f0',
        transition: 'all 0.2s'
      },
      '& .MuiOutlinedInput-root:hover fieldset': {
        borderColor: borderColor || (m === 'dark' ? '#4ADE80' : '#22C55E')
      },
      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: borderColor || '#22C55E',
        boxShadow: borderColor
          ? `0 0 0 3px ${borderColor}10`
          : '0 0 0 3px rgba(34, 197, 94, 0.1)'
      }
    }),
    textFieldBlue: (m = mode) => ({
      '& .MuiOutlinedInput-root': {
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        '& input': {
          padding: '0.875rem'
        }
      },
      '& .MuiInputLabel-root': {
        display: 'none'
      },
      '& fieldset': {
        borderWidth: '2px',
        borderColor: m === 'dark' ? '#333333' : '#e2e8f0',
        transition: 'all 0.2s'
      },
      '& .MuiOutlinedInput-root:hover fieldset': {
        borderColor: m === 'dark' ? '#60A5FA' : '#3B82F6'
      },
      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: '#3B82F6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
      }
    }),
    presentButtonEducation: (m = mode, isActive) => ({
      minWidth: '100px',
      height: '56px',
      borderRadius: '0.5rem',
      textTransform: 'none',
      fontWeight: 500,
      border: isActive
        ? '2px solid #22C55E'
        : m === 'dark' ? '2px solid #333333' : '2px solid #e2e8f0',
      backgroundColor: isActive
        ? (m === 'dark' ? 'rgba(74, 222, 128, 0.18)' : '#ecfdf5')
        : m === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white',
      color: isActive
        ? (m === 'dark' ? '#4ADE80' : '#22C55E')
        : m === 'dark' ? '#D4D4D8' : '#64748b',
      '&:hover': {
        backgroundColor: isActive
          ? (m === 'dark' ? 'rgba(74, 222, 128, 0.26)' : '#d1fae5')
          : (m === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f0fdf4'),
        borderColor: '#22C55E'
      }
    }),
    presentButtonExperience: (m = mode, isActive) => ({
      minWidth: '100px',
      height: '56px',
      borderRadius: '0.5rem',
      textTransform: 'none',
      fontWeight: 500,
      border: isActive
        ? '2px solid #3B82F6'
        : m === 'dark' ? '2px solid #333333' : '2px solid #e2e8f0',
      backgroundColor: isActive
        ? (m === 'dark' ? 'rgba(96, 165, 250, 0.18)' : '#eff6ff')
        : m === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white',
      color: isActive
        ? (m === 'dark' ? '#93C5FD' : '#3B82F6')
        : m === 'dark' ? '#D4D4D8' : '#64748b',
      '&:hover': {
        backgroundColor: isActive
          ? (m === 'dark' ? 'rgba(96, 165, 250, 0.26)' : '#dbeafe')
          : (m === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f8fafc'),
        borderColor: '#3B82F6'
      }
    }),
    saveButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#22C55E',
      color: 'white',
      border: 'none',
      borderRadius: '0.375rem',
      textTransform: 'none',
      fontSize: '0.875rem',
      '&:hover': {
        backgroundColor: '#16A34A'
      }
    },
    cancelButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '0.375rem',
      textTransform: 'none',
      fontSize: '0.875rem',
      '&:hover': {
        backgroundColor: '#4b5563'
      }
    },
    addButton: (m = mode) => ({
      borderRadius: '0.75rem',
      px: 4,
      py: 2,
      borderWidth: '2px',
      borderStyle: 'dashed',
      borderColor: '#9CA3AF',
      color: '#6B7280',
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: '#2563EB',
        color: '#2563EB',
        backgroundColor: m === 'dark' ? 'rgba(96, 165, 250, 0.1)' : '#EFF6FF',
        borderWidth: '2px',
        borderStyle: 'dashed',
      }
    }),
    displayCard: (m = mode) => ({
      mb: 3,
      borderRadius: '0.5rem',
      boxShadow: 'none',
      backgroundColor: m === 'dark' ? '#262626' : '#ffffff',
      border: m === 'dark' ? '1px solid #333333' : '1px solid #e2e8f0',
      transition: 'box-shadow 0.2s',
      position: 'relative',
      '&:hover': {
        boxShadow: m === 'dark'
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        '& .education-actions, & .experience-actions': {
          opacity: 1
        }
      }
    }),
    editActionButton: (m = mode) => ({
      p: '0.375rem',
      color: m === 'dark' ? '#60A5FA' : '#3B82F6',
      backgroundColor: m === 'dark' ? 'rgba(96, 165, 250, 0.15)' : '#eff6ff',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: m === 'dark' ? 'rgba(96, 165, 250, 0.25)' : '#dbeafe'
      }
    }),
    deleteActionButton: (m = mode) => ({
      p: '0.375rem',
      color: m === 'dark' ? '#f87171' : '#dc2626',
      backgroundColor: m === 'dark' ? 'rgba(220, 38, 38, 0.15)' : '#fef2f2',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: m === 'dark' ? 'rgba(220, 38, 38, 0.25)' : '#fee2e2'
      }
    }),
    sectionTitle: (m = mode) => ({
      fontSize: '1.5rem',
      fontWeight: 600,
      color: m === 'dark' ? '#EDEDED' : '#1F2937',
      mb: 3
    }),
    personalInfoField: (m = mode) => ({
      fontSize: '0.875rem',
      color: m === 'dark' ? '#EDEDED' : '#111827',
      fontWeight: 500,
      wordBreak: 'break-words',
      cursor: 'pointer',
      padding: '0.375rem',
      borderRadius: 1,
      transition: 'background-color 0.2s',
      '&:hover': {
        backgroundColor: m === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'
      }
    }),
    notProvidedText: (m = mode) => ({
      color: m === 'dark' ? '#64748b' : '#9ca3af',
      fontStyle: 'italic'
    }),
    settingsCard: (m = mode) => ({
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      backgroundColor: m === 'dark' ? '#262626' : '#ffffff',
      border: m === 'dark' ? '1px solid #333333' : '1px solid #E5E7EB',
      p: 4
    })
  };
}

// Export default theme (light mode)
export default theme;
